import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { CardHeader, CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { getCurrentSkuIdForProduct } from 'pricing-pages-lib/utils/productsUtils';
import { useId } from 'react';
import { shallowEqual } from 'react-redux';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { planBuilderProductLevelForProductLineUpdatedThunk } from '../../../actions/PlanBuilderActions';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { planBuilderIsLoadingSelector, planBuilderProductConfigurationsSelector } from '../../../selectors/PlanBuilderSelectors';
import { bundledProductLinesForSkuIdSelector } from '../../../selectors/ProductConfigsSelectors';
import { productForSkuIdSelector, quantityPackForProductVersionSelector, skuIdHasBundledProductsSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { productDataSelector } from '../../../selectors/SelectorUtils';
import { productNamesByProductLevelMapSelector } from '../../../selectors/TrackingSelectors';
import { seleniumUnitQuantitiesStringSelector, seleniumUnitsTypesStringSelector } from '../../../utils/planBuilderUtils/SeleniumUtils';
import { trackPlanBuilderInteraction } from '../../../v2/utils/tracking/trackingEventUtils';
import { isQuantityPackDetails, isValidProductLevel, isValidQuantityPackApiName } from '../../../v2/utils/typeguards';
import QuantityPickerContainer from '../QuantityPicker/QuantityPickerContainer';
import HubProductLevelPicker from './HubProductLevelPicker';
import PlanPickerHeader from './PlanPickerHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HubPlanPickerRow = ({
  productLine
}) => {
  const dispatch = useAppDispatch();
  const productConfigurations = useAppSelector(planBuilderProductConfigurationsSelector);
  const isLoading = useAppSelector(planBuilderIsLoadingSelector);
  const currencyCode = useDisplayedCurrencyCode();
  const productData = useAppSelector(productDataSelector);

  //which sku for this productLine should be selected
  const selectedSkuId = useAppSelector(state => {
    const foundConfig = productConfigurations.find(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      const hasBundleWithProductLine = skuIdHasBundledProductsSelector(state, {
        skuId
      }) && bundledProductLinesForSkuIdSelector(state, {
        skuId
      }).includes(productLine);
      return product.productLine === productLine && product.productLevel || hasBundleWithProductLine;
    });
    return foundConfig === null || foundConfig === void 0 ? void 0 : foundConfig.skuId;
  });
  const selectedProductLevel = useAppSelector(state => {
    if (!selectedSkuId) {
      return null;
    }
    const productLevel = productForSkuIdSelector(state, {
      skuId: selectedSkuId
    }).productLevel;
    return isValidProductLevel(productLevel) ? productLevel : null;
  });
  const additionalUnitSku = useAppSelector(state => {
    if (selectedSkuId) {
      const sku = skuSelector(state, {
        skuId: selectedSkuId
      });
      if (isQuantityPackDetails(sku)) {
        return sku;
      }
      const quantityPackApiName = productLine === MARKETING ? 'additional-marketing-contacts' : `additional-${productLine.toLowerCase()}-seats`;
      if (!isValidQuantityPackApiName(quantityPackApiName)) {
        return null;
      }
      const quantityPack = quantityPackForProductVersionSelector(state, {
        productVersionSkuId: selectedSkuId,
        quantityPackApiName
      });
      if (quantityPack) {
        return quantityPack;
      }
      return null;
    }
    return null;
  });
  const seleniumUnitsString = useAppSelector(state => seleniumUnitQuantitiesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const seleniumUnitsTypesIdString = useAppSelector(state => seleniumUnitsTypesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const apiName = useAppSelector(state => {
    const product = selectedSkuId && productForSkuIdSelector(state, {
      skuId: selectedSkuId
    });
    return product && product.apiName;
  });
  const hubProductLevelPickerLabelId = useId();
  const trackingProductNamesByProductLevelMap = useAppSelector(state => productNamesByProductLevelMapSelector(state, {
    productLine
  }), shallowEqual);
  const setProductLevel = newProductLevel => {
    if (newProductLevel) {
      trackPlanBuilderInteraction({
        component: 'HubPlanPickerList',
        action: 'clicked product level selection button',
        value: newProductLevel,
        productName: trackingProductNamesByProductLevelMap[newProductLevel],
        skuId: getCurrentSkuIdForProduct({
          productData
        }, {
          productLine,
          productLevel: newProductLevel
        }),
        activeCurrencyCode: currencyCode
      });
    }
    dispatch(planBuilderProductLevelForProductLineUpdatedThunk({
      productLine,
      productLevel: newProductLevel
    }));
  };
  const PlanPickerTitle = /*#__PURE__*/_jsx(PlanPickerHeader, {
    productLine: productLine,
    id: hubProductLevelPickerLabelId
  });
  return /*#__PURE__*/_jsxs(CardWrapper, {
    marginBlockEnd: "400",
    "data-product-level": selectedProductLevel,
    "data-selenium-id": `${productLine}-plan-builder-picker`,
    "data-api-name": apiName,
    "data-units": seleniumUnitsString || '0',
    "data-units-type": seleniumUnitsTypesIdString,
    children: [/*#__PURE__*/_jsx(CardHeader, {
      titleSize: "xs",
      title: PlanPickerTitle,
      paddingBlockEnd: "0"
    }), /*#__PURE__*/_jsxs(CardSection, {
      children: [/*#__PURE__*/_jsx(HubProductLevelPicker, {
        ariaLabelledById: hubProductLevelPickerLabelId,
        productLine: productLine,
        selectedProductLevel: selectedProductLevel,
        setSelectedProductLevel: setProductLevel,
        isLoading: isLoading
      }), /*#__PURE__*/_jsx(AccordionTransition, {
        open: !!additionalUnitSku,
        children: additionalUnitSku && /*#__PURE__*/_jsx(QuantityPickerContainer, {
          additionalUnitSku: additionalUnitSku
        })
      })]
    })]
  });
};
export default HubPlanPickerRow;